import fs from 'fs-extra';
import path from 'path';
import { WORKFLOW_DIR } from '../../constants.js';
// Simple ID generation function
function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
}

export interface Agent {
  id: string;
  name: string;
  role: string;
  description: string;
  avatar?: string;
  status: 'active' | 'inactive' | 'beta' | 'deprecated';
  category: 'development' | 'design' | 'testing' | 'operations' | 'management' | 'analysis';
  capabilities: string[];
  tools: string[];
  prompts: {
    system?: string;
    thinking?: string;
    output?: string;
    examples?: string[];
  };
  preferences?: {
    maxConcurrentTasks?: number;
    responseTime?: string;
    workingHours?: string;
  };
  collaboration?: {
    canDelegate?: boolean;
    reportingTo?: string;
    teamRoles?: string[];
  };
  metrics?: {
    tasksCompleted: number;
    successRate: number;
    averageTime: string;
    lastActive: string;
  };
  tags?: string[];
  version: string;
  created: string;
  updated: string;
}

export class AgentService {
  private agentsPath: string;

  constructor() {
    this.agentsPath = path.join(process.cwd(), WORKFLOW_DIR, 'agents.json');
    this.ensureAgentsFile();
  }

  private async ensureAgentsFile() {
    await fs.ensureFile(this.agentsPath);
    const exists = await fs.pathExists(this.agentsPath);
    if (!exists || (await fs.readFile(this.agentsPath, 'utf-8')).trim() === '') {
      // 初始化一些默认 Agent
      const defaultAgents: Agent[] = [
        {
          id: 'agent-frontend',
          name: '前端开发工程师',
          role: 'frontend_developer',
          description: '专注于前端开发，精通 React、Vue 和现代前端工具链',
          avatar: '👨‍💻',
          status: 'active',
          category: 'development',
          capabilities: ['coding', 'testing', 'debugging', 'reviewing'],
          tools: ['react', 'vue', 'vite', 'webpack', 'typescript'],
          prompts: {
            system: '你是一个专业的前端开发工程师，精通现代前端技术栈。',
            thinking: '分析需求 -> 设计组件结构 -> 实现功能 -> 优化性能',
            output: '提供清晰的代码实现和详细的注释说明'
          },
          preferences: {
            maxConcurrentTasks: 3,
            responseTime: 'immediate',
            workingHours: '24/7'
          },
          metrics: {
            tasksCompleted: 0,
            successRate: 0,
            averageTime: '0h',
            lastActive: new Date().toISOString()
          },
          tags: ['frontend', 'react', 'vue', 'ui'],
          version: '1.0.0',
          created: new Date().toISOString(),
          updated: new Date().toISOString()
        },
        {
          id: 'agent-backend',
          name: '后端开发工程师',
          role: 'backend_developer',
          description: '专注于后端开发，精通 Node.js、Python 和数据库设计',
          avatar: '🔧',
          status: 'active',
          category: 'development',
          capabilities: ['coding', 'testing', 'debugging', 'architecture'],
          tools: ['nodejs', 'python', 'postgresql', 'mongodb', 'redis'],
          prompts: {
            system: '你是一个专业的后端开发工程师，精通服务器端开发和数据库设计。',
            thinking: '分析需求 -> 设计 API -> 实现逻辑 -> 优化性能',
            output: '提供高效的代码实现和清晰的 API 文档'
          },
          preferences: {
            maxConcurrentTasks: 2,
            responseTime: '1h',
            workingHours: '24/7'
          },
          metrics: {
            tasksCompleted: 0,
            successRate: 0,
            averageTime: '0h',
            lastActive: new Date().toISOString()
          },
          tags: ['backend', 'nodejs', 'api', 'database'],
          version: '1.0.0',
          created: new Date().toISOString(),
          updated: new Date().toISOString()
        },
        {
          id: 'agent-tester',
          name: '测试工程师',
          role: 'qa_engineer',
          description: '专注于软件测试，精通自动化测试和质量保证',
          avatar: '🧪',
          status: 'active',
          category: 'testing',
          capabilities: ['testing', 'reviewing', 'reporting'],
          tools: ['jest', 'cypress', 'playwright', 'selenium'],
          prompts: {
            system: '你是一个专业的测试工程师，精通各种测试方法和工具。',
            thinking: '分析需求 -> 设计测试用例 -> 执行测试 -> 报告结果',
            output: '提供详细的测试报告和改进建议'
          },
          preferences: {
            maxConcurrentTasks: 5,
            responseTime: '30m',
            workingHours: '9-5'
          },
          metrics: {
            tasksCompleted: 0,
            successRate: 0,
            averageTime: '0h',
            lastActive: new Date().toISOString()
          },
          tags: ['testing', 'qa', 'automation'],
          version: '1.0.0',
          created: new Date().toISOString(),
          updated: new Date().toISOString()
        },
        {
          id: 'agent-architect',
          name: '架构师',
          role: 'software_architect',
          description: '专注于系统架构设计，精通分布式系统和微服务架构',
          avatar: '🏗️',
          status: 'active',
          category: 'design',
          capabilities: ['architecture', 'planning', 'reviewing'],
          tools: ['kubernetes', 'docker', 'aws', 'microservices'],
          prompts: {
            system: '你是一个专业的软件架构师，精通系统设计和架构模式。',
            thinking: '分析需求 -> 设计架构 -> 评估方案 -> 优化设计',
            output: '提供详细的架构设计文档和实施方案'
          },
          preferences: {
            maxConcurrentTasks: 1,
            responseTime: '1d',
            workingHours: '9-5'
          },
          collaboration: {
            canDelegate: true,
            teamRoles: ['frontend_developer', 'backend_developer']
          },
          metrics: {
            tasksCompleted: 0,
            successRate: 0,
            averageTime: '0h',
            lastActive: new Date().toISOString()
          },
          tags: ['architecture', 'design', 'system'],
          version: '1.0.0',
          created: new Date().toISOString(),
          updated: new Date().toISOString()
        },
        {
          id: 'agent-pm',
          name: '产品经理',
          role: 'product_manager',
          description: '专注于产品设计和需求分析，精通用户体验和产品规划',
          avatar: '📋',
          status: 'active',
          category: 'management',
          capabilities: ['planning', 'analyzing', 'communicating'],
          tools: ['jira', 'figma', 'notion', 'slack'],
          prompts: {
            system: '你是一个专业的产品经理，精通产品设计和用户体验。',
            thinking: '分析用户需求 -> 设计产品方案 -> 制定实施计划 -> 跟踪进度',
            output: '提供清晰的 PRD 文档和产品规划'
          },
          preferences: {
            maxConcurrentTasks: 4,
            responseTime: '2h',
            workingHours: '9-6'
          },
          collaboration: {
            canDelegate: true,
            teamRoles: ['designer', 'frontend_developer', 'backend_developer']
          },
          metrics: {
            tasksCompleted: 0,
            successRate: 0,
            averageTime: '0h',
            lastActive: new Date().toISOString()
          },
          tags: ['product', 'planning', 'requirement'],
          version: '1.0.0',
          created: new Date().toISOString(),
          updated: new Date().toISOString()
        }
      ];
      await fs.writeJson(this.agentsPath, defaultAgents, { spaces: 2 });
    }
  }

  async list(): Promise<Agent[]> {
    try {
      const agents = await fs.readJson(this.agentsPath);
      return agents;
    } catch (error) {
      console.error('Error reading agents:', error);
      return [];
    }
  }

  async get(id: string): Promise<Agent | null> {
    const agents = await this.list();
    return agents.find(agent => agent.id === id) || null;
  }

  async create(agentData: Omit<Agent, 'id' | 'created' | 'updated' | 'metrics'>): Promise<Agent> {
    const agents = await this.list();
    
    // 检查名称是否重复
    if (agents.some(agent => agent.name === agentData.name)) {
      throw new Error('Agent 名称已存在');
    }

    const newAgent: Agent = {
      ...agentData,
      id: `agent-${generateId()}`,
      metrics: {
        tasksCompleted: 0,
        successRate: 0,
        averageTime: '0h',
        lastActive: new Date().toISOString()
      },
      created: new Date().toISOString(),
      updated: new Date().toISOString()
    };

    agents.push(newAgent);
    await fs.writeJson(this.agentsPath, agents, { spaces: 2 });
    
    return newAgent;
  }

  async update(id: string, updates: Partial<Agent>): Promise<Agent> {
    const agents = await this.list();
    const index = agents.findIndex(agent => agent.id === id);
    
    if (index === -1) {
      throw new Error('Agent 不存在');
    }

    // 如果更新名称，检查是否重复
    if (updates.name && updates.name !== agents[index].name) {
      if (agents.some(agent => agent.name === updates.name && agent.id !== id)) {
        throw new Error('Agent 名称已存在');
      }
    }

    const updatedAgent = {
      ...agents[index],
      ...updates,
      id,
      created: agents[index].created,
      updated: new Date().toISOString()
    };

    agents[index] = updatedAgent;
    await fs.writeJson(this.agentsPath, agents, { spaces: 2 });
    
    return updatedAgent;
  }

  async delete(id: string): Promise<void> {
    const agents = await this.list();
    const filteredAgents = agents.filter(agent => agent.id !== id);
    
    if (filteredAgents.length === agents.length) {
      throw new Error('Agent 不存在');
    }

    await fs.writeJson(this.agentsPath, filteredAgents, { spaces: 2 });
  }

  // 更新 Agent 指标
  async updateMetrics(id: string, metrics: Partial<Agent['metrics']>): Promise<void> {
    const agents = await this.list();
    const agent = agents.find(a => a.id === id);
    
    if (agent && agent.metrics) {
      Object.assign(agent.metrics, metrics);
      agent.metrics.lastActive = new Date().toISOString();
      agent.updated = new Date().toISOString();
      await fs.writeJson(this.agentsPath, agents, { spaces: 2 });
    }
  }
}